import { getCreditPool } from '@/lib/api';
import { useEffect, useState, useRef } from 'react';

export default function CreditProgress() {
  const [creditPoolData, setCreditPoolData] = useState({
    poolSize: 0,
    totalUsedCredits: 0,
    loading: true,
  });
  const [animated, setAnimated] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Fetch credit pool data
  useEffect(() => {
    const fetchCreditPoolData = async () => {
      try {
        const data = await getCreditPool();
        if (data) {
          setCreditPoolData({
            poolSize: data.poolSize ?? 0,
            totalUsedCredits: data.totalUsedCredits ?? 0,
            loading: false,
          });
        } else {
          setCreditPoolData((prev) => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error fetching credit pool:', error);
        setCreditPoolData((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchCreditPoolData();
  }, []);

  useEffect(() => {
    if (creditPoolData.loading || !progressBarRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimated(true);
            observer.disconnect(); // Only trigger once
          }
        });
      },
      { threshold: 0.1 } // Trigger when at least 10% is visible
    );

    observer.observe(progressBarRef.current);

    return () => {
      observer.disconnect();
    };
  }, [creditPoolData.loading]);

  if (creditPoolData.loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="h-12 bg-gray-200 rounded-full animate-pulse"></div>
      </div>
    );
  }

  const percentUnused =
    creditPoolData.poolSize > 0
      ? Math.min(
          100,
          Math.round(
            100 - creditPoolData.totalUsedCredits / creditPoolData.poolSize
          )
        )
      : 0;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="h-8 w-full bg-gray-200 rounded-full overflow-hidden shadow-inner border border-gray-300 relative">
        <div
          ref={progressBarRef}
          className="h-full bg-heineken-light-green transition-all duration-1500 ease-out relative"
          style={{
            width: animated ? `${percentUnused}%` : '100%',
            minWidth: '10px',
          }}
        ></div>
      </div>
      <p className="text-heineken mt-3">
        Fennmaradó közös promóciós keret állása:{' '}
        <span className="font-bold text-heineken-light-green">
          {percentUnused}
        </span>
        %
      </p>
    </div>
  );
}
